import {
  batteryConcertAmountErrorMessage,
  isRefreshPaymentRequiredError,
} from "./battery-financing-errors"

/**
 * Stable codes the Concert Battery application routes return in the error
 * body's `error` field (docs/api/battery-concert-applications.md).
 */
export const BATTERY_CONCERT_ERROR_CODES = [
  `BATTERY_FINANCING_LOCKED`,
  `CONCERT_APPLICATION_EXISTS`,
  `CONCERT_DOCUMENT_PENDING`,
  `CONCERT_SETUP_FAILED`,
  `CONCERT_SETUP_REQUIRED`,
  `CONCERT_SETUP_UNCONFIRMED`,
] as const

export type BatteryConcertErrorCode =
  (typeof BATTERY_CONCERT_ERROR_CODES)[number]

const isKnownCode = (value: unknown): value is BatteryConcertErrorCode =>
  typeof value === `string` &&
  (BATTERY_CONCERT_ERROR_CODES as readonly string[]).includes(value)

const responseData = (error: unknown): Record<string, unknown> | null => {
  if (error == null || typeof error !== `object` || !(`response` in error)) {
    return null
  }
  const { response } = error
  if (
    response == null ||
    typeof response !== `object` ||
    !(`data` in response)
  ) {
    return null
  }
  const { data } = response
  return data != null && typeof data === `object`
    ? (data as Record<string, unknown>)
    : null
}

export const batteryConcertErrorCode = (
  error: unknown,
): BatteryConcertErrorCode | null => {
  const code = responseData(error)?.error
  return isKnownCode(code) ? code : null
}

/** The server's plain-English message, when it sent one. */
export const batteryConcertErrorMessage = (error: unknown): string | null => {
  const message = responseData(error)?.message
  if (typeof message === `string` && message.trim().length > 0) {
    return message
  }
  if (
    Array.isArray(message) &&
    message.length > 0 &&
    message.every((entry) => typeof entry === `string`)
  ) {
    return message.join(`; `)
  }
  return null
}

export const CONCERT_SETUP_FAILED_MESSAGE = `The Concert customer could not be created. Contact support to correct the customer details before trying again.`
export const CONCERT_SETUP_UNCONFIRMED_MESSAGE = `Concert customer setup is not confirmed. Contact support before trying again.`
export const CONCERT_DOCUMENT_PENDING_MESSAGE = `This document upload is awaiting confirmation. Refresh status before retrying, or contact support if it stays pending.`
export const BATTERY_FINANCING_LOCKED_MESSAGE = `An Existing Concert Loan Application locks financing edits on this proposal.`
export const CONCERT_REFRESH_PRICING_REQUIRED_MESSAGE = `Pricing changed since this Quote was priced. Refresh Pricing, then submit again.`

/**
 * Copy for a failed customer initiation. Setup failures keep the form closed
 * and send the seller to support; nothing here links out to a provider.
 */
export const concertInitiationErrorMessage = (error: unknown): string => {
  switch (batteryConcertErrorCode(error)) {
    case `CONCERT_SETUP_FAILED`:
      return CONCERT_SETUP_FAILED_MESSAGE
    case `CONCERT_SETUP_UNCONFIRMED`:
      return CONCERT_SETUP_UNCONFIRMED_MESSAGE
    case `BATTERY_FINANCING_LOCKED`:
      return BATTERY_FINANCING_LOCKED_MESSAGE
    default:
      if (isRefreshPaymentRequiredError(error)) {
        return CONCERT_REFRESH_PRICING_REQUIRED_MESSAGE
      }
      // An amount refusal is not a setup failure, and its message already
      // names the bound and the price. Show it rather than sending the seller
      // to support for something they can fix themselves.
      return (
        batteryConcertAmountErrorMessage(error) ?? CONCERT_SETUP_FAILED_MESSAGE
      )
  }
}
